import React from "react";
import {FileImageOutlined, FileProtectOutlined, LoadingOutlined, WarningOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../../common/sectionCard/SectionCard";
import {buildJobRowModel, truncateSha} from "../../audit.model";
import styles from "../../styles/document-audit.module.scss";

/**
 * Stored-bytes preview for the selected job. S3-retained jobs render the
 * blob behind an object URL (images via `<img>`, PDFs via `<iframe>`);
 * hash-only jobs render the sha256 receipt empty-state. The object URL
 * lifecycle (create/revoke) is owned by the page controller.
 *
 * @param {Object} props
 * @param {Object} props.job — the selected job row
 * @param {{objectUrl: string | null, mimeType: string | null, loading: boolean, error: {title: string, detail: string} | null}} props.preview
 */
function DocumentPreview({job, preview}) {
    const model = buildJobRowModel(job);

    let body;
    if (!model.retained) {
        body = (
            <div className={styles.previewEmpty}>
                <FileProtectOutlined className={styles.previewEmptyIcon} />
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.previewEmptyTitle}>
                    Bytes not retained in this deployment — hash-only receipt
                </Text>
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.previewEmptyDetail}>
                    sha256{" "}
                    <span className={styles.mono} title={job?.sha256 || undefined}>
                        {truncateSha(job?.sha256)}
                    </span>
                </Text>
            </div>
        );
    } else if (preview?.loading) {
        body = (
            <div className={styles.previewEmpty}>
                <LoadingOutlined className={styles.previewEmptyIcon} />
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.previewEmptyTitle}>
                    Fetching the stored bytes from S3…
                </Text>
            </div>
        );
    } else if (preview?.error) {
        body = (
            <div className={styles.previewEmpty}>
                <WarningOutlined className={`${styles.previewEmptyIcon} ${styles.previewErrorIcon}`} />
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.previewEmptyTitle}>
                    {preview.error.title}
                </Text>
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.previewEmptyDetail}>
                    {preview.error.detail}
                </Text>
            </div>
        );
    } else if (preview?.objectUrl && (preview.mimeType || "").startsWith("image/")) {
        body = (
            <div className={styles.previewImageWrap}>
                <img src={preview.objectUrl} alt={job?.filename || "Uploaded document"} className={styles.previewImage} />
            </div>
        );
    } else if (preview?.objectUrl && preview.mimeType === "application/pdf") {
        body = <iframe src={preview.objectUrl} title={job?.filename || "Uploaded document"} className={styles.previewFrame} />;
    } else if (preview?.objectUrl) {
        body = (
            <div className={styles.previewEmpty}>
                <WarningOutlined className={styles.previewEmptyIcon} />
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.previewEmptyTitle}>
                    Preview not supported for {preview.mimeType || "this content type"}
                </Text>
            </div>
        );
    } else {
        body = null;
    }

    return (
        <SectionCard icon={<FileImageOutlined />} title="Stored document">
            {body}
        </SectionCard>
    );
}

export default DocumentPreview;
